import React, { useMemo, useState } from 'react';
import {
  Box,
  Typography,
  useTheme,
  alpha,
  ButtonBase,
  Collapse,
  Tooltip,
} from '@mui/material';
import FilterListRoundedIcon from '@mui/icons-material/FilterListRounded';
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';
import { useResponsive } from '../hooks/useResponsive';
import { SectionHeader } from '../components/ui/SectionHeader';
import { SurfaceCard } from '../components/ui/SurfaceCard';
import { StatusPill, useToneColor } from '../components/ui/StatusPill';
import { ChainAvatar, chainName, chainOf } from '../components/ui/ChainAvatar';
import { tokens } from '../theme';
import { formatPairPrice } from '../utils/formatters';
import { botLongLabel, botShortLabel, breakAfterPair, familyOf, needsChain } from '../utils/botNames';
import { IBotData, IPosition, IBalancingStep } from '../shared/types';

// ============================================================================
// TYPES
// ============================================================================

type TimelineEventType = 'position-opened' | 'position-closed' | 'balancing-step';

interface TimelineEvent {
  id: string;
  type: TimelineEventType;
  timestamp: number;
  botId: string;
  network: string;
  /** The bot's pair and price precision, for its prices ("$2,690.80", "0.0002310 WETH"). */
  pair: { token0: string; token1: string; decimals: number };
  data: {
    positionId?: number;
    price?: number;
    rangeLower?: number;
    rangeUpper?: number;
    stepType?: string;
    pnlUSD?: number;
    premiumUSD?: number;
    status?: string;
    duration?: number;
  };
}

// ============================================================================
// HELPERS
// ============================================================================

const formatDate = (timestamp: number) => {
  const date = new Date(timestamp);
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);

  if (date.toDateString() === today.toDateString()) {
    return 'Today';
  }
  if (date.toDateString() === yesterday.toDateString()) {
    return 'Yesterday';
  }

  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
  });
};

const formatTime = (timestamp: number) => {
  const date = new Date(timestamp);
  return date.toLocaleTimeString('en-US', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
};

const formatDuration = (ms: number) => {
  const hours = Math.floor(ms / (1000 * 60 * 60));
  const minutes = Math.floor((ms % (1000 * 60 * 60)) / (1000 * 60));

  if (hours > 24) {
    const days = Math.floor(hours / 24);
    const remainingHours = hours % 24;
    return `${days}d ${remainingHours}h`;
  }

  if (hours > 0) {
    return `${hours}h ${minutes}m`;
  }

  return `${minutes}m`;
};

const formatCurrency = (value: number) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
};

const extractNetworkFromBotId = (botId: string): string => {
  if (botId.startsWith('RSK')) return 'RSK';
  if (botId.startsWith('ARB')) return 'ARB';
  if (botId.startsWith('ETH')) return 'ETH';
  return 'Unknown';
};

/** How the timeline names its bots (rows and filter chips alike). */
interface BotNaming {
  /** Every event belongs to one family: bots read "Bot 2". */
  familyScoped: boolean;
  /** The same pair runs on several chains here: "USDC / WETH · Arbitrum · Bot 2". */
  withChain: boolean;
}

const botLabel = (botId: string, { familyScoped, withChain }: BotNaming) =>
  familyScoped ? botShortLabel(botId) : botLongLabel(botId, { chain: withChain });

/**
 * Bot names, one convention: "Bot 2" when the timeline covers a single family (the pair is already
 * on screen); "USDC / WETH · Bot 2" with a chain logo across families, and the chain spelled out
 * ("USDC / WETH · Arbitrum · Bot 2") when a pair runs on several chains. The raw id stays in the title.
 * Phones drop the logo when the chain is spelled out (the room goes to the name); a name that is still
 * too long wraps after the pair rather than losing the bot number to an ellipsis.
 */
const BotName: React.FC<{ botId: string; naming: BotNaming }> = ({ botId, naming }) => (
  <Box title={botId} sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.75, minWidth: 0 }}>
    {!naming.familyScoped && (
      <Box sx={{ display: { xs: naming.withChain ? 'none' : 'flex', sm: 'flex' }, mt: '4px', flexShrink: 0 }}>
        <ChainAvatar chain={chainOf(botId)} size={14} />
      </Box>
    )}
    <Typography sx={{ fontSize: '0.875rem', lineHeight: '22px', fontWeight: 650, letterSpacing: '-0.005em', minWidth: 0 }}>
      {breakAfterPair(botLabel(botId, naming))}
    </Typography>
  </Box>
);

// ============================================================================
// DATA PROCESSING
// ============================================================================

const processTimelineEvents = (bots: IBotData[]): TimelineEvent[] => {
  const events: TimelineEvent[] = [];
  const twoWeeksAgo = Date.now() - (14 * 24 * 60 * 60 * 1000);

  bots.forEach((bot) => {
    const botId = bot.stats.botName;
    const network = extractNetworkFromBotId(botId);
    const pair = { token0: bot.stats.token0Symbol, token1: bot.stats.token1Symbol, decimals: bot.stats.decimals };

    bot.positions?.forEach((position: IPosition) => {
      if (position.date >= twoWeeksAgo) {
        events.push({
          id: `${botId}-position-opened-${position.positionId}-${position.date}`,
          type: 'position-opened',
          timestamp: position.date,
          botId,
          network,
          pair,
          data: {
            positionId: position.positionId,
            price: position.entryPrice,
            rangeLower: position.rangeLower,
            rangeUpper: position.rangeUpper,
          },
        });
      }

      if (!position.isOpen && position.endDate && position.endDate >= twoWeeksAgo) {
        events.push({
          id: `${botId}-position-closed-${position.positionId}-${position.endDate}`,
          type: 'position-closed',
          timestamp: position.endDate,
          botId,
          network,
          pair,
          data: {
            positionId: position.positionId,
            price: position.currentPrice,
            duration: position.endDate - position.date,
          },
        });
      }
    });

    const balancingSteps = (bot as any).balancingSteps as IBalancingStep[] | undefined;
    balancingSteps?.forEach((step: IBalancingStep) => {
      if (step.date >= twoWeeksAgo) {
        events.push({
          id: `${botId}-balancing-${step.date}`,
          type: 'balancing-step',
          timestamp: step.date,
          botId,
          network,
          pair,
          data: {
            stepType: step.stepDescription || step.stepType || 'Unknown',
            pnlUSD: step.pnlUSD,
            premiumUSD: step.premiumUSD,
            status: step.status,
            duration: step.endDate ? step.endDate - step.date : undefined,
          },
        });
      }
    });
  });

  return events.sort((a, b) => b.timestamp - a.timestamp);
};

// ============================================================================
// TIMELINE NODE COMPONENT (Single Event)
// ============================================================================

interface TimelineNodeProps {
  event: TimelineEvent;
  isLast: boolean;
  isMobile: boolean;
  naming: BotNaming;
}

const EVENT_CONFIG: Record<TimelineEventType, { tone: 'success' | 'neutral' | 'brand'; label: string }> = {
  'position-opened': { tone: 'success', label: 'Opened' },
  'position-closed': { tone: 'neutral', label: 'Closed' },
  // Balancing is "in progress" on the status scale: gold (brand), not amber.
  'balancing-step': { tone: 'brand', label: 'Balancing' },
};

/** Venues and step names arrive in CAPS from the bots: show them in sentence case. */
const sentenceCase = (s?: string) =>
  s && s === s.toUpperCase() ? s.charAt(0) + s.slice(1).toLowerCase() : s ?? '';

const EVENTS_PREVIEW = 5;

const TimelineNode: React.FC<TimelineNodeProps> = ({ event, isLast, isMobile, naming }) => {
  const theme = useTheme();
  const config = EVENT_CONFIG[event.type];
  const toneColor = useToneColor()(config.tone);
  const { token0, token1, decimals } = event.pair;

  const meta: React.ReactNode[] = [`${formatDate(event.timestamp)} · ${formatTime(event.timestamp)}`];
  if (event.type !== 'balancing-step' && event.data.positionId) meta.push(`#${event.data.positionId}`);
  if (event.type === 'position-opened' && event.data.price) {
    // Same price format as the bot's own cards: "$0.5477", "$2,690.80", "0.0002310 WETH".
    meta.push(`@ ${formatPairPrice(event.data.price, token0, token1, decimals)}`);
  }
  if (event.data.duration) meta.push(formatDuration(event.data.duration));
  if (event.type === 'balancing-step' && event.data.stepType) meta.push(sentenceCase(event.data.stepType));

  return (
    <Box sx={{ display: 'flex', position: 'relative' }}>
      {/* Rail */}
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mr: isMobile ? 1.5 : 1.75, pt: '7px' }}>
        <Box
          sx={{
            width: 8,
            height: 8,
            borderRadius: '50%',
            bgcolor: toneColor,
            boxShadow: `0 0 10px ${alpha(toneColor, 0.75)}`,
            flexShrink: 0,
          }}
        />
        {!isLast && <Box sx={{ width: '1px', flex: 1, mt: 1, bgcolor: 'rgba(255,255,255,0.08)' }} />}
      </Box>

      {/* Event row */}
      <Box
        sx={{
          flex: 1,
          minWidth: 0,
          pb: isLast ? 0 : 1.75,
          mb: isLast ? 0 : 1.75,
          borderBottom: isLast ? 'none' : `1px solid ${tokens.borderSubtle}`,
        }}
      >
        {/* Top-aligned: a name that wraps keeps the pill on its first line. */}
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1 }}>
          <BotName botId={event.botId} naming={naming} />
          <StatusPill tone={config.tone} label={config.label} size="sm" />
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'baseline', flexWrap: 'wrap', columnGap: 0.75, rowGap: 0.25, mt: 0.5 }}>
          <Typography component="span" sx={{ fontSize: '0.75rem', color: 'text.secondary', fontVariantNumeric: 'tabular-nums' }}>
            {meta.join(' · ')}
          </Typography>
          {event.type === 'balancing-step' && event.data.premiumUSD !== undefined && event.data.premiumUSD > 0 && (
            <Typography component="span" sx={{ fontSize: '0.75rem', fontWeight: 700, color: theme.palette.success.main, fontVariantNumeric: 'tabular-nums' }}>
              +{formatCurrency(event.data.premiumUSD)}
            </Typography>
          )}
        </Box>
      </Box>
    </Box>
  );
};

// ============================================================================
// COLLAPSIBLE NETWORK SECTION
// ============================================================================

interface NetworkSectionProps {
  network: 'RSK' | 'ARB' | 'ETH';
  events: TimelineEvent[];
  isMobile: boolean;
  naming: BotNaming;
  defaultExpanded?: boolean;
}

const NetworkSection: React.FC<NetworkSectionProps> = ({ network, events, isMobile, naming, defaultExpanded = true }) => {
  const [expanded, setExpanded] = useState(defaultExpanded);
  const [showAll, setShowAll] = useState(false);
  const visible = showAll ? events : events.slice(0, EVENTS_PREVIEW);

  return (
    <SurfaceCard padding="none" tint={tokens.chainTint[network]} sx={{ alignSelf: 'start' }}>
      <ButtonBase
        onClick={() => setExpanded(!expanded)}
        aria-expanded={expanded}
        sx={{
          width: '100%',
          px: { xs: 2.5, md: 3 },
          py: { xs: 2, md: 2.25 },
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          borderBottom: expanded ? `1px solid ${tokens.borderSubtle}` : 'none',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
          <ChainAvatar chain={network} size={28} />
          <Typography sx={{ fontSize: '1rem', fontWeight: 650, letterSpacing: '-0.01em' }}>
            {chainName(network)}
          </Typography>
          <Box
            component="span"
            sx={{
              px: 1,
              height: 22,
              display: 'inline-flex',
              alignItems: 'center',
              borderRadius: 999,
              fontSize: '0.75rem',
              fontWeight: 650,
              color: 'text.secondary',
              bgcolor: 'rgba(255,255,255,0.06)',
              border: `1px solid ${tokens.border}`,
              fontVariantNumeric: 'tabular-nums',
            }}
          >
            {events.length}
          </Box>
        </Box>
        <ExpandMoreRoundedIcon
          sx={{
            fontSize: 20,
            color: 'text.secondary',
            transition: 'transform 0.2s ease',
            transform: expanded ? 'rotate(0)' : 'rotate(-90deg)',
          }}
        />
      </ButtonBase>

      <Collapse in={expanded}>
        <Box sx={{ px: { xs: 2.5, md: 3 }, py: 2 }}>
          {events.length === 0 ? (
            <Typography sx={{ py: 3, textAlign: 'center', fontSize: '0.8125rem', color: 'text.secondary' }}>
              No events in the last 2 weeks
            </Typography>
          ) : (
            <>
              {visible.map((event, index) => (
                <TimelineNode
                  key={event.id}
                  event={event}
                  isLast={index === visible.length - 1}
                  isMobile={isMobile}
                  naming={naming}
                />
              ))}
              {events.length > EVENTS_PREVIEW && (
                <ButtonBase
                  onClick={() => setShowAll((v) => !v)}
                  sx={{
                    mt: 2,
                    width: '100%',
                    height: 40,
                    '@media (pointer: coarse)': { height: 44 },
                    borderRadius: 999,
                    fontSize: '0.8125rem',
                    fontWeight: 650,
                    color: 'text.secondary',
                    bgcolor: 'rgba(255,255,255,0.04)',
                    border: `1px solid ${tokens.borderSubtle}`,
                    '&:hover': { color: 'text.primary', bgcolor: 'rgba(255,255,255,0.07)' },
                  }}
                >
                  {showAll ? 'Show less' : `Show all ${events.length}`}
                </ButtonBase>
              )}
            </>
          )}
        </Box>
      </Collapse>
    </SurfaceCard>
  );
};

// ============================================================================
// FILTER CHIP COMPONENT
// ============================================================================

interface FilterChipProps {
  label: string;
  selected: boolean;
  onClick: () => void;
  /** Chain of the bot (mini logo). */
  chain?: string;
  /** Tooltip (the raw bot id). */
  title?: string;
}

const FilterChip: React.FC<FilterChipProps> = ({ label, selected, onClick, chain, title }) => (
  <ButtonBase
    onClick={onClick}
    aria-pressed={selected}
    title={title}
    sx={{
      gap: 0.75,
      pl: chain ? 0.75 : 1.5,
      pr: 1.5,
      height: 32,
      '@media (pointer: coarse)': { height: 44 },
      flexShrink: 0,
      borderRadius: 999,
      fontSize: '0.75rem',
      fontWeight: 650,
      whiteSpace: 'nowrap',
      color: selected ? 'text.primary' : 'text.secondary',
      bgcolor: selected ? 'rgba(255,255,255,0.13)' : 'rgba(255,255,255,0.03)',
      border: `1px solid ${selected ? 'rgba(255,255,255,0.22)' : tokens.borderSubtle}`,
      boxShadow: selected ? 'inset 0 1px 0 rgba(255,255,255,0.12)' : 'none',
      transition: 'background-color 140ms ease, color 140ms ease, border-color 140ms ease',
      '&:hover': { color: 'text.primary', bgcolor: selected ? 'rgba(255,255,255,0.16)' : 'rgba(255,255,255,0.06)' },
    }}
  >
    {chain && <ChainAvatar chain={chain} size={20} />}
    {label}
  </ButtonBase>
);

// ============================================================================
// MAIN TIMELINE VIEW COMPONENT
// ============================================================================

interface TimelineViewProps {
  bots: IBotData[];
}

const TimelineView: React.FC<TimelineViewProps> = ({ bots }) => {
  const { isMobile } = useResponsive();
  const [selectedBot, setSelectedBot] = useState<string>('ALL');
  const [showFilters, setShowFilters] = useState(!isMobile);

  const allEvents = useMemo(() => processTimelineEvents(bots), [bots]);
  const eventBots = useMemo(() => Array.from(new Set(allEvents.map((e) => e.botId))).sort(), [allEvents]);
  // One family on screen (the pair is already named): "Bot 2". Across families: "USDC / WETH · Bot 2",
  // or "USDC / WETH · Arbitrum · Bot 2" once the same pair shows up on two chains (rows and chips alike).
  const familyScoped = useMemo(() => new Set(bots.map((b) => familyOf(b.stats.botName))).size === 1, [bots]);
  const naming = useMemo<BotNaming>(
    () => ({ familyScoped, withChain: !familyScoped && needsChain(eventBots) }),
    [familyScoped, eventBots]
  );

  // Separate events by network
  const rskEvents = useMemo(() => {
    let events = allEvents.filter((e) => e.network === 'RSK');
    if (selectedBot !== 'ALL') events = events.filter((e) => e.botId === selectedBot);
    return events;
  }, [allEvents, selectedBot]);

  const arbEvents = useMemo(() => {
    let events = allEvents.filter((e) => e.network === 'ARB');
    if (selectedBot !== 'ALL') events = events.filter((e) => e.botId === selectedBot);
    return events;
  }, [allEvents, selectedBot]);

  const ethEvents = useMemo(() => {
    let events = allEvents.filter((e) => e.network === 'ETH');
    if (selectedBot !== 'ALL') events = events.filter((e) => e.botId === selectedBot);
    return events;
  }, [allEvents, selectedBot]);

  const availableBots = useMemo(() => ['ALL', ...eventBots], [eventBots]);

  const totalFilteredEvents = rskEvents.length + arbEvents.length + ethEvents.length;

  return (
    <Box>
      <SectionHeader
        variant="page"
        title="Activity"
        subtitle={`Last 14 days · ${totalFilteredEvents} events`}
        action={
          isMobile ? (
            <Tooltip title="Toggle filters">
              <ButtonBase
                onClick={() => setShowFilters(!showFilters)}
                aria-label="Toggle filters"
                aria-expanded={showFilters}
                sx={{
                  width: 44,
                  height: 44,
                  borderRadius: 999,
                  bgcolor: showFilters ? 'rgba(255,255,255,0.13)' : 'rgba(255,255,255,0.04)',
                  border: `1px solid ${tokens.border}`,
                  color: 'text.secondary',
                }}
              >
                <FilterListRoundedIcon sx={{ fontSize: 18 }} />
              </ButtonBase>
            </Tooltip>
          ) : undefined
        }
      />

      {/* Bot filters: one scrollable row */}
      <Collapse in={showFilters}>
        <Box
          role="group"
          aria-label="Filter by bot"
          sx={{
            display: 'flex',
            gap: 0.75,
            overflowX: 'auto',
            mb: { xs: 2, md: 3 },
            mx: { xs: -2, sm: 0 },
            px: { xs: 2, sm: 0 },
            pb: 0.5,
            scrollbarWidth: 'none',
            '&::-webkit-scrollbar': { display: 'none' },
            maskImage: 'linear-gradient(90deg, #000 calc(100% - 32px), transparent)',
          }}
        >
          {availableBots.map((botId) => (
            <FilterChip
              key={botId}
              label={botId === 'ALL' ? 'All bots' : botLabel(botId, naming)}
              title={botId === 'ALL' ? undefined : botId}
              selected={selectedBot === botId}
              onClick={() => setSelectedBot(botId)}
              chain={botId === 'ALL' || naming.familyScoped ? undefined : extractNetworkFromBotId(botId)}
            />
          ))}
        </Box>
      </Collapse>

      {/* Timeline grid */}
      <Box
        sx={{
          display: 'grid',
          // Three columns only on wide screens: on iPad (1194px) they truncate the bot ids.
          gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: 'repeat(2, minmax(0, 1fr))', xl: 'repeat(3, minmax(0, 1fr))' },
          gap: { xs: 1.5, md: 2 },
          alignItems: 'start',
        }}
      >
        <NetworkSection network="RSK" events={rskEvents} isMobile={isMobile} naming={naming} />
        <NetworkSection network="ARB" events={arbEvents} isMobile={isMobile} naming={naming} />
        <NetworkSection network="ETH" events={ethEvents} isMobile={isMobile} naming={naming} />
      </Box>
    </Box>
  );
};

export default TimelineView;
